import React, { useState, useMemo } from "react";

import { CollapsibleSection } from "../CollapsibleSection";
import { formatDisplayDate } from "../../../helpers/dataHelper";
import { formatCurrency } from "../viewerHelpers";
import Field from "./Field";
import StatusBadge from "./StatusBadge";
import ExpandCollapseToggle from "./ExpandCollapseToggle";

// ===== Raw 279 (270/271 Eligibility) Viewer =====

interface Raw279Eligibility {
  traceNumber?: string;
  subscriberName?: string;
  subscriberId?: string;
  dependentName?: string;
  dependentBirthDate?: string;
  providerName?: string;
  providerId?: string;
  payerName?: string;
  payerId?: string;
  serviceDate?: string;
  requestType?: "inquiry" | "response";
  eligibilityInfo?: Array<{
    serviceType?: string;
    serviceTypeCode?: string;
    coverageLevel?: string;
    insuranceType?: string;
    benefitAmount?: number;
    benefitPercent?: number;
    timePeriod?: string;
    quantity?: number;
    authRequired?: boolean;
    inPlanNetwork?: string;
    message?: string;
  }>;
}

const parseRaw279Data = (data: unknown[]): Raw279Eligibility[] => {
  const eligibilities: Raw279Eligibility[] = [];

  // Find ST segment (transaction set header)
  const stSegment = data.find(
    (seg: unknown) =>
      (seg as { segment_id?: string }).segment_id === "ST" &&
      ["270", "271"].includes(
        (seg as { transaction_set_identifier_code?: { code?: string } })
          .transaction_set_identifier_code?.code || "",
      ),
  ) as {
    transaction_set_identifier_code?: { code?: string };
    information_source_level?: Array<{
      information_source_name?: {
        response_contact_last_or_organization_name?: string;
        response_contact_identifier?: string;
      };
      information_receiver_level?: Array<{
        information_receiver_name?: {
          response_contact_last_or_organization_name?: string;
          response_contact_first_name?: string;
          response_contact_identifier?: string;
        };
        subscriber_level?: Array<{
          subscriber_name?: {
            response_contact_last_or_organization_name?: string;
            response_contact_first_name?: string;
            response_contact_identifier?: string;
          };
          dependent_level?: Array<{
            dependent_name?: {
              response_contact_last_or_organization_name?: string;
              response_contact_first_name?: string;
              dependent_demographic_information?: {
                dependent_birth_date?: string;
              };
            };
            dependent_trace_number?: Array<{
              current_transaction_trace_number?: string;
            }>;
            dependent_eligibility_or_benefit_information?: Array<{
              eligibility_or_benefit_information_code?: {
                code?: string;
                desc?: string;
              };
              coverage_level_code?: { code?: string; desc?: string };
              service_type_code?: { code?: string; desc?: string };
              insurance_type_code?: { code?: string; desc?: string };
              time_period_qualifier?: { code?: string; desc?: string };
              monetary_amount?: number;
              percent?: number;
              quantity_qualifier?: { code?: string };
              quantity?: number;
              yes_no_condition_or_response_code?: { code?: string };
              in_plan_network_indicator?: { code?: string; desc?: string };
              MSG?: Array<{ free_form_message_text?: string }>;
            }>;
          }>;
          subscriber_eligibility_or_benefit_information?: Array<{
            eligibility_or_benefit_information_code?: {
              code?: string;
              desc?: string;
            };
            coverage_level_code?: { code?: string; desc?: string };
            service_type_code?: { code?: string; desc?: string };
            insurance_type_code?: { code?: string; desc?: string };
            time_period_qualifier?: { code?: string; desc?: string };
            monetary_amount?: number;
            percent?: number;
            quantity_qualifier?: { code?: string };
            quantity?: number;
            yes_no_condition_or_response_code?: { code?: string };
            in_plan_network_indicator?: { code?: string; desc?: string };
            MSG?: Array<{ free_form_message_text?: string }>;
          }>;
        }>;
      }>;
    }>;
  };

  if (!stSegment) return eligibilities;

  const isResponse = stSegment.transaction_set_identifier_code?.code === "271";
  const requestType = isResponse ? "response" : "inquiry";

  const infoSourceLevels = stSegment.information_source_level || [];

  for (const infoSource of infoSourceLevels) {
    const payerName =
      infoSource.information_source_name
        ?.response_contact_last_or_organization_name;
    const payerId =
      infoSource.information_source_name?.response_contact_identifier;

    const infoReceiverLevels = infoSource.information_receiver_level || [];

    for (const infoReceiver of infoReceiverLevels) {
      const providerName = [
        infoReceiver.information_receiver_name?.response_contact_first_name,
        infoReceiver.information_receiver_name
          ?.response_contact_last_or_organization_name,
      ]
        .filter(Boolean)
        .join(" ");
      const providerId =
        infoReceiver.information_receiver_name?.response_contact_identifier;

      const subscriberLevels = infoReceiver.subscriber_level || [];

      for (const subscriber of subscriberLevels) {
        const subName = subscriber.subscriber_name;
        const subscriberName = [
          subName?.response_contact_first_name,
          subName?.response_contact_last_or_organization_name,
        ]
          .filter(Boolean)
          .join(" ");
        const subscriberId = subName?.response_contact_identifier;

        // Process subscriber-level eligibility
        const subEligInfo = (
          subscriber.subscriber_eligibility_or_benefit_information || []
        ).map((eb) => ({
          serviceType: eb.service_type_code?.desc,
          serviceTypeCode: eb.service_type_code?.code,
          coverageLevel: eb.coverage_level_code?.desc,
          insuranceType: eb.insurance_type_code?.desc,
          benefitAmount: eb.monetary_amount,
          benefitPercent: eb.percent,
          timePeriod: eb.time_period_qualifier?.desc,
          quantity: eb.quantity,
          authRequired: eb.yes_no_condition_or_response_code?.code === "Y",
          inPlanNetwork: eb.in_plan_network_indicator?.desc,
          message: eb.MSG?.[0]?.free_form_message_text,
        }));

        if (subEligInfo.length > 0 || !subscriber.dependent_level?.length) {
          eligibilities.push({
            payerName,
            payerId,
            providerName,
            providerId,
            subscriberName,
            subscriberId,
            requestType,
            eligibilityInfo: subEligInfo,
          });
        }

        // Process dependent-level eligibility
        const dependentLevels = subscriber.dependent_level || [];
        for (const dependent of dependentLevels) {
          const depName = dependent.dependent_name;
          const dependentName = [
            depName?.response_contact_first_name,
            depName?.response_contact_last_or_organization_name,
          ]
            .filter(Boolean)
            .join(" ");
          const dependentBirthDate =
            depName?.dependent_demographic_information?.dependent_birth_date;
          const traceNumber =
            dependent.dependent_trace_number?.[0]
              ?.current_transaction_trace_number;

          const depEligInfo = (
            dependent.dependent_eligibility_or_benefit_information || []
          ).map((eb) => ({
            serviceType: eb.service_type_code?.desc,
            serviceTypeCode: eb.service_type_code?.code,
            coverageLevel: eb.coverage_level_code?.desc,
            insuranceType: eb.insurance_type_code?.desc,
            benefitAmount: eb.monetary_amount,
            benefitPercent: eb.percent,
            timePeriod: eb.time_period_qualifier?.desc,
            quantity: eb.quantity,
            authRequired: eb.yes_no_condition_or_response_code?.code === "Y",
            inPlanNetwork: eb.in_plan_network_indicator?.desc,
            message: eb.MSG?.[0]?.free_form_message_text,
          }));

          eligibilities.push({
            traceNumber,
            payerName,
            payerId,
            providerName,
            providerId,
            subscriberName,
            subscriberId,
            dependentName,
            dependentBirthDate,
            requestType,
            eligibilityInfo: depEligInfo,
          });
        }
      }
    }
  }

  return eligibilities;
};

const Raw279Viewer: React.FC<{ data: unknown[] }> = ({ data }) => {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [allExpanded, setAllExpanded] = useState(true);

  const eligibilities = useMemo(() => parseRaw279Data(data), [data]);

  const selectedItem =
    selectedIndex !== null ? eligibilities[selectedIndex] : null;

  // Determine if this is a 270 request or 271 response
  const stSegment = data.find(
    (seg: unknown) => (seg as { segment_id?: string }).segment_id === "ST",
  ) as { transaction_set_identifier_code?: { code?: string } } | undefined;
  const transactionType =
    stSegment?.transaction_set_identifier_code?.code || "270";
  const isResponse = transactionType === "271";
  const title = isResponse
    ? "271 Eligibility Response"
    : "270 Eligibility Inquiry";

  if (eligibilities.length === 0) {
    return (
      <div className="p-4 text-sm text-gray-500">
        No eligibility information found in this {transactionType} file.
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full w-full bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-3">
        <div className="flex items-center flex-wrap gap-2">
          <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
          <StatusBadge status="ELIGIBILITY" type="type" />
          <span className="text-sm text-gray-500">
            {eligibilities.length} record(s)
          </span>
          <div className="flex-1" />
          {selectedItem && (
            <button
              onClick={() => setSelectedIndex(null)}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              Back to list
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-4 scrollbar-thin">
        {!selectedItem ? (
          // List view
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr className="text-sm text-gray-500 uppercase">
                    <th className="px-3 py-2 text-left">#</th>
                    <th className="px-3 py-2 text-left">Patient</th>
                    <th className="px-3 py-2 text-left">Subscriber ID</th>
                    <th className="px-3 py-2 text-left">Payer</th>
                    <th className="px-3 py-2 text-left">Provider</th>
                    <th className="px-3 py-2 text-center">Benefits</th>
                  </tr>
                </thead>
                <tbody>
                  {eligibilities.map((elig, idx) => (
                    <tr
                      key={idx}
                      className={`border-b border-gray-100 cursor-pointer hover:bg-blue-50 ${idx === selectedIndex ? "bg-blue-100" : ""}`}
                      onClick={() => setSelectedIndex(idx)}
                    >
                      <td className="px-3 py-2 text-md font-medium">
                        {idx + 1}
                      </td>
                      <td className="px-3 py-2 text-md">
                        {elig.dependentName || elig.subscriberName || "-"}
                        {elig.dependentName && (
                          <span className="text-sm text-gray-500 ml-1">
                            (Dependent)
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-md font-mono">
                        {elig.subscriberId || "-"}
                      </td>
                      <td className="px-3 py-2 text-md text-gray-600">
                        {elig.payerName || "-"}
                      </td>
                      <td className="px-3 py-2 text-md text-gray-600">
                        {elig.providerName || "-"}
                      </td>
                      <td className="px-3 py-2 text-md text-center">
                        <span className="px-2 py-0.5 bg-pink-100 text-pink-800 rounded-full text-sm">
                          {elig.eligibilityInfo?.length || 0}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          // Detail view
          <div>
            <div className="mb-4 flex items-center gap-2">
              <ExpandCollapseToggle
                expanded={allExpanded}
                onToggle={setAllExpanded}
              />
            </div>

            <CollapsibleSection
              title="Request Information"
              defaultOpen={allExpanded}
            >
              <div
                className="grid gap-4"
                style={{
                  gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                }}
              >
                <Field label="Trace Number" value={selectedItem.traceNumber} />
                <Field
                  label="Request Type"
                  value={
                    selectedItem.requestType === "response"
                      ? "Response"
                      : "Inquiry"
                  }
                />
              </div>
            </CollapsibleSection>

            <CollapsibleSection
              title="Patient / Subscriber"
              defaultOpen={allExpanded}
            >
              <div
                className="grid gap-4"
                style={{
                  gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                }}
              >
                <Field label="Subscriber" value={selectedItem.subscriberName} />
                <Field
                  label="Subscriber ID"
                  value={selectedItem.subscriberId}
                />
                {selectedItem.dependentName && (
                  <>
                    <Field
                      label="Dependent (Patient)"
                      value={selectedItem.dependentName}
                    />
                    <Field
                      label="Dependent Birth Date"
                      value={formatDisplayDate(selectedItem.dependentBirthDate)}
                    />
                  </>
                )}
              </div>
            </CollapsibleSection>

            <CollapsibleSection
              title="Payer / Provider"
              defaultOpen={allExpanded}
            >
              <div
                className="grid gap-4"
                style={{
                  gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                }}
              >
                <Field label="Payer" value={selectedItem.payerName} />
                <Field label="Payer ID" value={selectedItem.payerId} />
                <Field label="Provider" value={selectedItem.providerName} />
                <Field label="Provider ID" value={selectedItem.providerId} />
              </div>
            </CollapsibleSection>

            {selectedItem.eligibilityInfo &&
              selectedItem.eligibilityInfo.length > 0 && (
                <CollapsibleSection
                  title={`Eligibility / Benefits (${selectedItem.eligibilityInfo.length})`}
                  defaultOpen={allExpanded}
                >
                  <div className="space-y-3">
                    {selectedItem.eligibilityInfo.map((benefit, idx) => (
                      <div key={idx} className="p-3 bg-white rounded border">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-gray-900">
                            {benefit.serviceType ||
                              benefit.serviceTypeCode ||
                              "Benefit"}
                          </span>
                          {benefit.inPlanNetwork && (
                            <span
                              className={`px-2 py-0.5 rounded text-sm ${
                                benefit.inPlanNetwork
                                  .toLowerCase()
                                  .includes("in")
                                  ? "bg-green-100 text-green-800"
                                  : "bg-yellow-100 text-yellow-800"
                              }`}
                            >
                              {benefit.inPlanNetwork}
                            </span>
                          )}
                        </div>
                        <div
                          className="grid gap-3 text-sm"
                          style={{
                            gridTemplateColumns:
                              "repeat(auto-fit, minmax(120px, 1fr))",
                          }}
                        >
                          {benefit.coverageLevel && (
                            <div>
                              <span className="text-sm text-gray-500">
                                Coverage Level
                              </span>
                              <div>{benefit.coverageLevel}</div>
                            </div>
                          )}
                          {benefit.insuranceType && (
                            <div>
                              <span className="text-sm text-gray-500">
                                Insurance Type
                              </span>
                              <div>{benefit.insuranceType}</div>
                            </div>
                          )}
                          {benefit.benefitAmount !== undefined && (
                            <div>
                              <span className="text-sm text-gray-500">
                                Amount
                              </span>
                              <div className="font-medium">
                                {formatCurrency(benefit.benefitAmount)}
                              </div>
                            </div>
                          )}
                          {benefit.benefitPercent !== undefined && (
                            <div>
                              <span className="text-sm text-gray-500">
                                Percent
                              </span>
                              <div>{benefit.benefitPercent}%</div>
                            </div>
                          )}
                          {benefit.timePeriod && (
                            <div>
                              <span className="text-sm text-gray-500">
                                Time Period
                              </span>
                              <div>{benefit.timePeriod}</div>
                            </div>
                          )}
                          {benefit.quantity !== undefined && (
                            <div>
                              <span className="text-sm text-gray-500">
                                Quantity
                              </span>
                              <div>{benefit.quantity}</div>
                            </div>
                          )}
                          {benefit.authRequired !== undefined && (
                            <div>
                              <span className="text-sm text-gray-500">
                                Auth Required
                              </span>
                              <div>{benefit.authRequired ? "Yes" : "No"}</div>
                            </div>
                          )}
                        </div>
                        {benefit.message && (
                          <div className="mt-2 text-sm text-gray-600 italic border-t pt-2">
                            {benefit.message}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </CollapsibleSection>
              )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Raw279Viewer;
